package com.arenaproplay.admin.pages.system

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import com.arenaproplay.admin.domain.admin.ApiErrorRow
import com.arenaproplay.admin.domain.admin.ApiPlatformBreakdown
import com.arenaproplay.admin.domain.admin.ApiRequestStats
import com.arenaproplay.admin.ui.components.admin.*
import com.arenaproplay.admin.ui.theme.AppColors
import com.arenaproplay.admin.ui.theme.eyebrow
import com.arenaproplay.admin.ui.theme.numSmall
import com.arenaproplay.admin.ui.theme.toRgbString
import com.arenaproplay.admin.ui.utils.sBorderRadius
import org.jetbrains.compose.web.css.*

// ── Constants ─────────────────────────────────────────────────────────────────

// API table column widths (px)
const val SYS_W_METHOD = 60
const val SYS_W_PATH = 360
const val SYS_W_NUM = 80
const val SYS_W_LATENCY = 80

val SYS_KNOWN_PLATFORMS = listOf("web", "ios", "android")
val SYS_HIDDEN_PLATFORMS = setOf("admin-web", "server", "unknown")
val SYS_PLATFORM_ACCENT = mapOf(
    "web" to "#4D8CFF",  // Night Arena primary light
    "ios" to "#0BAB64",
    "android" to "#C7D2FE",
)

// ── Data model ────────────────────────────────────────────────────────────────

data class SysEndpointRow(
    val method: String,
    val path: String,
    val total: Long,
    val avgLatencyMs: Double,
    val maxLatencyMs: Int,
    val errors: Long,
    val byPlatform: Map<String, Long>,
) {
    val errorRate: Double get() = if (total == 0L) 0.0 else errors.toDouble() / total
}

fun sysBuildRows(
    totals: List<ApiRequestStats>,
    breakdown: List<ApiPlatformBreakdown>,
    errors: List<ApiErrorRow>,
): List<SysEndpointRow> {
    val errorsByKey = errors
        .groupBy { "${it.method}|${it.path}" }
        .mapValues { (_, rows) -> rows.sumOf { it.count } }
    val breakdownByKey = breakdown.associate { ("${it.method}|${it.path}") to it.byPlatform }
    return totals.map { stat ->
        val key = "${stat.method}|${stat.path}"
        SysEndpointRow(
            method = stat.method,
            path = stat.path,
            total = stat.count,
            avgLatencyMs = stat.avgLatencyMs,
            maxLatencyMs = stat.maxLatencyMs,
            errors = errorsByKey[key] ?: stat.errorCount,
            byPlatform = breakdownByKey[key] ?: emptyMap(),
        )
    }.sortedByDescending { it.total }
}

// ── Private helpers ───────────────────────────────────────────────────────────

private fun sysMethodPillTone(method: String): PillTone = when (method.uppercase()) {
    "GET" -> PillTone.Accent
    "POST" -> PillTone.Success
    "PUT", "PATCH" -> PillTone.Warning
    "DELETE" -> PillTone.Danger
    else -> PillTone.Neutral
}

private fun sysErrorRatePillTone(errorRate: Double): PillTone = when {
    errorRate >= 0.05 -> PillTone.Danger
    errorRate >= 0.01 -> PillTone.Warning
    else -> PillTone.Success
}

private fun sysStatusCodePillTone(status: Int): PillTone = when {
    status in 500..599 -> PillTone.Danger
    status in 400..499 -> PillTone.Warning
    else -> PillTone.Neutral
}

// ─────────────────────────────────────────────────────────────────────────────
// TAB 3: API
// ─────────────────────────────────────────────────────────────────────────────

@Composable
fun SystemApiTab(
    loadState: LoadState,
    totals: List<ApiRequestStats>?,
    rows: List<SysEndpointRow>,
    activePlatforms: List<String>,
    errors: List<ApiErrorRow>?,
    totalRequests: Long,
    totalErrors: Long,
    avgLatencyMs: Double,
    maxLatencyMs: Int,
) {
    if (loadState.error != null) {
        ErrorCard(loadState)
        return
    }

    // KPI strip
    val kpisLoaded = totals != null
    Row(Modifier.fillMaxWidth().styleModifier { property("gap", "12px") }) {
        KpiCard(
            label = "Запросов",
            value = if (kpisLoaded) formatNumber(totalRequests) else "—",
            accentColor = AppColors.Primary,
            modifier = Modifier.weight(1f),
        )
        KpiCard(
            label = "Ошибок (4xx/5xx)",
            value = if (kpisLoaded) formatNumber(totalErrors) else "—",
            accentColor = AppColors.Error,
            modifier = Modifier.weight(1f),
        )
        KpiCard(
            label = "Ср. задержка",
            value = if (kpisLoaded) "${avgLatencyMs.toInt()} мс" else "—",
            accentColor = AppColors.Faint,
            modifier = Modifier.weight(1f),
        )
        KpiCard(
            label = "Макс. задержка",
            value = if (kpisLoaded) "$maxLatencyMs мс" else "—",
            accentColor = AppColors.Warning,
            modifier = Modifier.weight(1f),
        )
    }

    VSpace(20)

    // Endpoints × platforms table
    SectionCard(
        title = "Эндпоинты × платформы",
        subtitle = "Трафик, задержка и ошибки по эндпоинту и платформе клиента",
    ) {
        SysHorizontalScroll {
            SysEndpointsTable(rows, activePlatforms, loading = totals == null)
        }
    }

    VSpace(16)

    // Errors table
    SectionCard(
        title = "Ошибки (4xx / 5xx)",
        subtitle = "HTTP-ошибки по эндпоинту и коду статуса",
    ) {
        SysHorizontalScroll {
            SysErrorsTable(errors, loading = errors == null)
        }
    }

    VSpace(36)
}

// ── Horizontal scroll wrapper ─────────────────────────────────────────────────

@Composable
private fun SysHorizontalScroll(content: @Composable () -> Unit) {
    Box(
        Modifier.fillMaxWidth().overflow(Overflow.Auto)
            .styleModifier { property("scrollbar-gutter", "stable") },
    ) {
        content()
    }
}

// ── Endpoints table ───────────────────────────────────────────────────────────

@Composable
private fun SysEndpointsTable(
    rows: List<SysEndpointRow>,
    platforms: List<String>,
    loading: Boolean,
) {
    var sortCol by remember { mutableStateOf("total") }
    var sortAsc by remember { mutableStateOf(false) }
    fun sortBy(col: String) {
        if (sortCol == col) sortAsc = !sortAsc else { sortCol = col; sortAsc = false }
    }

    val sorted = remember(rows, platforms, sortCol, sortAsc) {
        val comparator: Comparator<SysEndpointRow> = when {
            sortCol == "path" -> compareBy { it.path }
            sortCol == "avg" -> compareBy { it.avgLatencyMs }
            sortCol == "max" -> compareBy { it.maxLatencyMs }
            sortCol == "err" -> compareBy { it.errors }
            sortCol == "errRate" -> compareBy { it.errorRate }
            sortCol.startsWith("platform:") -> {
                val p = sortCol.removePrefix("platform:")
                compareBy { it.byPlatform[p] ?: 0L }
            }
            else -> compareBy { it.total }
        }
        rows.sortedWith(if (sortAsc) comparator else comparator.reversed())
    }
    fun mark(col: String): String? = if (sortCol == col) (if (sortAsc) "↑" else "↓") else null

    val tableMinWidth = SYS_W_METHOD + SYS_W_PATH + SYS_W_NUM +
        (platforms.size * SYS_W_NUM) + (SYS_W_LATENCY * 2) + SYS_W_NUM + SYS_W_NUM

    Column(Modifier.minWidth(tableMinWidth.px).fillMaxWidth()) {
        // Sticky header
        Row(
            Modifier.fillMaxWidth().padding(leftRight = 20.px, topBottom = 10.px)
                .styleModifier {
                    property("position", "sticky")
                    property("top", "0")
                    property("background", AppColors.Surface.toRgbString())
                    property("z-index", "1")
                    property("border-bottom", "1px solid ${AppColors.Border.toRgbString()}")
                    property("min-height", "36px")
                },
            verticalAlignment = Alignment.CenterVertically,
        ) {
            SysEpHeaderCell("Метод", SYS_W_METHOD)
            SysEpHeaderCell("Эндпоинт", SYS_W_PATH, sortMark = mark("path"), onSort = { sortBy("path") })
            SysEpHeaderCell("Всего", SYS_W_NUM, alignRight = true, sortMark = mark("total"), onSort = { sortBy("total") })
            platforms.forEach { p ->
                SysEpHeaderCell(
                    p.replaceFirstChar { c -> c.uppercase() }, SYS_W_NUM,
                    color = SYS_PLATFORM_ACCENT[p],
                    alignRight = true,
                    sortMark = mark("platform:$p"),
                    onSort = { sortBy("platform:$p") },
                )
            }
            SysEpHeaderCell("Ср. мс", SYS_W_LATENCY, alignRight = true, sortMark = mark("avg"), onSort = { sortBy("avg") })
            SysEpHeaderCell("Макс. мс", SYS_W_LATENCY, alignRight = true, sortMark = mark("max"), onSort = { sortBy("max") })
            SysEpHeaderCell("Ошибки", SYS_W_NUM, alignRight = true, sortMark = mark("err"), onSort = { sortBy("err") })
            SysEpHeaderCell("% ошибок", SYS_W_NUM, alignRight = true, sortMark = mark("errRate"), onSort = { sortBy("errRate") })
        }

        when {
            loading -> repeat(8) { i -> SysEpSkeletonRow(platforms, isLast = i == 7) }
            rows.isEmpty() -> EmptyState(
                icon = "◌",
                title = "Нет запросов за период",
                hint = "Данные появятся, как только поступят API-запросы",
            )
            else -> sorted.forEachIndexed { i, row ->
                SysEndpointDataRow(row, platforms, isLast = i == sorted.size - 1)
            }
        }
    }
}

@Composable
private fun SysEpSkeletonRow(platforms: List<String>, isLast: Boolean) {
    val colCount = 7 + platforms.size
    Row(
        Modifier.fillMaxWidth().padding(leftRight = 20.px, topBottom = 10.px)
            .styleModifier {
                if (!isLast) property("border-bottom", "1px solid ${AppColors.Border.toRgbString()}")
                property("min-height", "36px")
            },
        verticalAlignment = Alignment.CenterVertically,
    ) {
        repeat(colCount) { i ->
            val widthPx = when (i) { 0 -> SYS_W_METHOD; 1 -> SYS_W_PATH; else -> SYS_W_NUM }
            Box(Modifier.width(widthPx.px).minWidth(widthPx.px).padding(right = 10.px)) {
                Box(
                    Modifier.height(13.px).sBorderRadius(6)
                        .styleModifier {
                            property("width", "70%")
                            property("background", "rgba(255,255,255,.05)")
                            property("animation", "admin-skeleton 1.4s ease-in-out infinite")
                        }
                )
            }
        }
    }
}

@Composable
private fun SysEndpointDataRow(row: SysEndpointRow, platforms: List<String>, isLast: Boolean) {
    Row(
        Modifier.fillMaxWidth().padding(leftRight = 20.px, topBottom = 9.px)
            .classNames("arena-table-row")
            .styleModifier {
                if (!isLast) property("border-bottom", "1px solid ${AppColors.Border.toRgbString()}")
                property("min-height", "36px")
                property("transition", "background 140ms ease")
            },
        verticalAlignment = Alignment.CenterVertically,
    ) {
        SysEpCell(SYS_W_METHOD) { Pill(row.method.uppercase(), sysMethodPillTone(row.method)) }
        SysEpCell(SYS_W_PATH) {
            SpanText(
                row.path,
                Modifier.fontSize(12.5.px).color(AppColors.Ink).styleModifier {
                    property("font-family", "JetBrains Mono, monospace")
                    property("white-space", "nowrap")
                    property("overflow", "hidden")
                    property("text-overflow", "ellipsis")
                    property("max-width", "${SYS_W_PATH - 18}px")
                    property("display", "block")
                },
            )
        }
        SysEpNumCell(SYS_W_NUM, formatNumber(row.total), AppColors.Ink.toRgbString())
        platforms.forEach { p ->
            val v = row.byPlatform[p] ?: 0L
            SysEpNumCell(
                SYS_W_NUM,
                if (v == 0L) "·" else formatNumber(v),
                if (v == 0L) AppColors.Faint.toRgbString() else (SYS_PLATFORM_ACCENT[p] ?: AppColors.Muted.toRgbString()),
            )
        }
        SysEpNumCell(SYS_W_LATENCY, "${row.avgLatencyMs.toInt()} мс", AppColors.Muted.toRgbString())
        SysEpNumCell(SYS_W_LATENCY, "${row.maxLatencyMs} мс", AppColors.Muted.toRgbString())
        SysEpNumCell(
            SYS_W_NUM,
            if (row.errors == 0L) "·" else formatNumber(row.errors),
            if (row.errors == 0L) AppColors.Faint.toRgbString() else AppColors.Error.toRgbString(),
        )
        SysEpCell(SYS_W_NUM) {
            if (row.total > 0) {
                val pct = row.errorRate * 100
                val label = if (pct < 0.1) "<0.1%" else "${pct.toInt()}%"
                Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
                    Pill(label, sysErrorRatePillTone(row.errorRate))
                }
            } else {
                Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
                    SpanText("·", Modifier.numSmall().color(AppColors.Faint))
                }
            }
        }
    }
}

// ── Errors table ──────────────────────────────────────────────────────────────

@Composable
private fun SysErrorsTable(errors: List<ApiErrorRow>?, loading: Boolean) {
    val tableMinWidth = SYS_W_METHOD + SYS_W_PATH + SYS_W_NUM + SYS_W_NUM
    Column(Modifier.minWidth(tableMinWidth.px).fillMaxWidth()) {
        Row(
            Modifier.fillMaxWidth().padding(leftRight = 20.px, topBottom = 10.px)
                .styleModifier {
                    property("position", "sticky")
                    property("top", "0")
                    property("background", AppColors.Surface.toRgbString())
                    property("z-index", "1")
                    property("border-bottom", "1px solid ${AppColors.Border.toRgbString()}")
                    property("min-height", "36px")
                },
            verticalAlignment = Alignment.CenterVertically,
        ) {
            SysEpHeaderCell("Метод", SYS_W_METHOD)
            SysEpHeaderCell("Эндпоинт", SYS_W_PATH)
            SysEpHeaderCell("Статус", SYS_W_NUM, alignRight = true)
            SysEpHeaderCell("Кол-во", SYS_W_NUM, alignRight = true)
        }
        when {
            loading -> repeat(6) { i ->
                Row(
                    Modifier.fillMaxWidth().padding(leftRight = 20.px, topBottom = 10.px)
                        .styleModifier {
                            if (i < 5) property("border-bottom", "1px solid ${AppColors.Border.toRgbString()}")
                            property("min-height", "36px")
                        },
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    listOf(SYS_W_METHOD, SYS_W_PATH, SYS_W_NUM, SYS_W_NUM).forEachIndexed { j, w ->
                        Box(Modifier.width(w.px).minWidth(w.px).padding(right = 10.px)) {
                            Box(
                                Modifier.height(13.px).sBorderRadius(6).styleModifier {
                                    property("width", if (j == 1) "60%" else "50%")
                                    property("background", "rgba(255,255,255,.05)")
                                    property("animation", "admin-skeleton 1.4s ease-in-out infinite")
                                }
                            )
                        }
                    }
                }
            }
            errors.isNullOrEmpty() -> EmptyState(
                icon = "✓",
                title = "Нет ошибок за период",
                hint = "4xx и 5xx ответы появятся здесь, если возникнут",
            )
            else -> errors.sortedByDescending { it.count }.forEachIndexed { i, e ->
                Row(
                    Modifier.fillMaxWidth().padding(leftRight = 20.px, topBottom = 9.px)
                        .classNames("arena-table-row")
                        .styleModifier {
                            if (i < errors.size - 1) property("border-bottom", "1px solid ${AppColors.Border.toRgbString()}")
                            property("min-height", "36px")
                            property("transition", "background 140ms ease")
                        },
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    SysEpCell(SYS_W_METHOD) { Pill(e.method.uppercase(), sysMethodPillTone(e.method)) }
                    SysEpCell(SYS_W_PATH) {
                        SpanText(
                            e.path,
                            Modifier.fontSize(12.5.px).color(AppColors.Ink).styleModifier {
                                property("font-family", "JetBrains Mono, monospace")
                                property("white-space", "nowrap")
                                property("overflow", "hidden")
                                property("text-overflow", "ellipsis")
                                property("max-width", "${SYS_W_PATH - 18}px")
                                property("display", "block")
                            },
                        )
                    }
                    SysEpCell(SYS_W_NUM) {
                        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
                            Pill(e.status.toString(), sysStatusCodePillTone(e.status))
                        }
                    }
                    SysEpNumCell(SYS_W_NUM, formatNumber(e.count), AppColors.Error.toRgbString())
                }
            }
        }
    }
}

// ── Shared table cell primitives ──────────────────────────────────────────────

@Composable
private fun SysEpCell(widthPx: Int, content: @Composable () -> Unit) {
    Box(Modifier.width(widthPx.px).minWidth(widthPx.px)) { content() }
}

@Composable
private fun SysEpNumCell(widthPx: Int, text: String, colorHex: String) {
    Box(Modifier.width(widthPx.px).minWidth(widthPx.px).padding(right = 10.px)) {
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
            SpanText(text, Modifier.numSmall().color(Color(colorHex)))
        }
    }
}

@Composable
private fun SysEpHeaderCell(
    label: String,
    widthPx: Int,
    color: String? = null,
    alignRight: Boolean = false,
    sortMark: String? = null,
    onSort: (() -> Unit)? = null,
) {
    Box(
        Modifier.width(widthPx.px).minWidth(widthPx.px)
            .then(if (alignRight) Modifier.padding(right = 10.px) else Modifier)
            .then(
                if (onSort != null) Modifier.cursor(Cursor.Pointer).onClick { onSort() }
                else Modifier
            ),
    ) {
        Row(
            Modifier.fillMaxWidth(),
            horizontalArrangement = if (alignRight) Arrangement.End else Arrangement.Start,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            SpanText(
                label,
                Modifier.eyebrow().color(
                    if (color != null) Color(color)
                    else if (sortMark != null) AppColors.Ink
                    else AppColors.Faint
                ).styleModifier {
                    property("font-weight", if (sortMark != null) "600" else "500")
                    property("white-space", "nowrap")
                },
            )
            if (onSort != null) {
                Box(Modifier.width(4.px))
                SpanText(
                    sortMark ?: "↕",
                    Modifier.fontSize(10.px)
                        .color(if (sortMark != null) AppColors.Ink else AppColors.Faint),
                )
            }
        }
    }
}
